/**
 * Unified Planning Service
 * 
 * Frontend API client for cross-system planning functionality:
 * - Generate grocery lists from scheduled date ranges
 * - Link scheduling to occasions
 * - Menu plan ↔ schedule integration
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

/**
 * Generate grocery list from scheduled meals in a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {Object} options - Generation options
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Generated grocery list
 */
export async function generateGroceryListFromSchedule(startDate, endDate, options = {}, token) {
    const response = await fetch(`${API_BASE}/planning/grocery-list-from-schedule`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            startDate,
            endDate,
            mealTypes: options.mealTypes,
            excludePantryStaples: options.excludePantryStaples,
            createNewIfLocked: options.createNewIfLocked
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate grocery list from schedule');
    }

    return response.json();
}

/**
 * Schedule a meal with full context
 * @param {number} recipeId - Recipe ID
 * @param {string} date - Schedule date (YYYY-MM-DD)
 * @param {Object} options - Scheduling options
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Created scheduled meal
 */
export async function scheduleMealWithContext(recipeId, date, options = {}, token) {
    const response = await fetch(`${API_BASE}/planning/schedule-meal`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            recipeId,
            date,
            occasionId: options.occasionId,
            occasionRecipeId: options.occasionRecipeId,
            mealType: options.mealType,
            servingsOverride: options.servingsOverride
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to schedule meal');
    }

    return response.json();
}

/**
 * Bulk schedule all recipes from an occasion onto calendar
 * @param {string} occasionId - Occasion ID
 * @param {string} startDate - First date to schedule (YYYY-MM-DD)
 * @param {Array<string>} mealTypes - Meal types to assign
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Scheduling result
 */
export async function bulkScheduleOccasion(occasionId, startDate, mealTypes, token) {
    const response = await fetch(`${API_BASE}/planning/bulk-schedule-occasion`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            occasionId,
            startDate,
            mealTypes
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to bulk schedule occasion');
    }

    return response.json();
}

/**
 * Create an occasion from scheduled meals in a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} name - Occasion name
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Created occasion
 */
export async function createOccasionFromSchedule(startDate, endDate, name, token) {
    const response = await fetch(`${API_BASE}/planning/occasion-from-schedule`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            startDate,
            endDate,
            name
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create occasion from schedule');
    }

    return response.json();
}

/**
 * Create a menu plan from scheduled meals
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} name - Plan name
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Created menu plan
 */
export async function createMenuPlanFromSchedule(startDate, endDate, name, token) {
    const response = await fetch(`${API_BASE}/planning/menu-plan-from-schedule`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            startDate,
            endDate,
            name
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create menu plan from schedule');
    }

    return response.json();
}

/**
 * Schedule a menu plan's recipes onto the calendar
 * @param {string} menuPlanId - Menu plan ID
 * @param {string} startDate - First date to schedule (YYYY-MM-DD)
 * @param {Array<string>} mealTypes - Meal types to assign
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Scheduling result
 */
export async function scheduleMenuPlan(menuPlanId, startDate, mealTypes, token) {
    const response = await fetch(`${API_BASE}/planning/schedule-menu-plan`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            menuPlanId,
            startDate,
            mealTypes
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to schedule menu plan');
    }

    return response.json();
}

/**
 * Lock or unlock a grocery list
 * @param {number} listId - Grocery list ID
 * @param {boolean} locked - Lock status
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Updated list
 */
export async function setGroceryListLock(listId, locked, token) {
    const response = await fetch(`${API_BASE}/planning/grocery-list/${listId}/lock`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ locked })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update grocery list lock');
    }

    return response.json();
}

/**
 * Get menu plans overlapping with a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} token - Firebase auth token
 * @returns {Promise<Array>} Menu plans
 */
export async function getMenuPlansForRange(startDate, endDate, token) {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`${API_BASE}/planning/menu-plans-for-range?${params}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get menu plans');
    }

    return response.json();
}

/**
 * Get source information for a grocery list
 * @param {number} listId - Grocery list ID
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Source information
 */
export async function getGroceryListSource(listId, token) {
    const response = await fetch(`${API_BASE}/planning/grocery-list/${listId}/source`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get grocery list source');
    }

    return response.json();
}

/**
 * Get unified planning hub data
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Hub data with meals, list, plans
 */
export async function getPlanningHubData(token) {
    const response = await fetch(`${API_BASE}/planning/hub`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get planning hub data');
    }

    return response.json();
}

/**
 * Generate grocery list from scheduled meals for a single day
 * @param {string} date - Target date (YYYY-MM-DD)
 * @param {Object} options - Generation options
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Generated grocery list
 */
export async function generateGroceryListFromDay(date, options = {}, token) {
    const response = await fetch(`${API_BASE}/planning/grocery-list-from-day`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            date,
            mealTypes: options.mealTypes,
            forceNew: options.forceNew
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate grocery list from day');
    }

    return response.json();
}

/**
 * Get daily grocery list summaries for a date range
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Daily summaries
 */
export async function getGroceryListsByDay(startDate, endDate, token) {
    const params = new URLSearchParams({ start: startDate, end: endDate });
    const response = await fetch(`${API_BASE}/planning/grocery-lists-by-day?${params}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get grocery lists by day');
    }

    return response.json();
}

/**
 * Get all related data for an occasion (grocery lists, scheduled meals, menu plans)
 * @param {string} occasionId - Occasion ID
 * @param {string} token - Firebase auth token
 * @returns {Promise<Object>} Occasion with related data
 */
export async function getOccasionRelatedData(occasionId, token) {
    const response = await fetch(`${API_BASE}/planning/occasion/${occasionId}/related-data`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get occasion related data');
    }

    return response.json();
}

export default {
    generateGroceryListFromSchedule,
    scheduleMealWithContext,
    bulkScheduleOccasion,
    createOccasionFromSchedule,
    createMenuPlanFromSchedule,
    scheduleMenuPlan,
    setGroceryListLock,
    getMenuPlansForRange,
    getGroceryListSource,
    getPlanningHubData,
    generateGroceryListFromDay,
    getGroceryListsByDay
};

